import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  FormGroup,
  Divider,
  Button,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  useTheme
} from '@mui/material';
import { 
  Save as SaveIcon,
  Print as PrintIcon,
  Receipt as ReceiptIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  AccountBalance as TaxIcon,
  CreditCard as PaymentIcon
} from '@mui/icons-material';
import { POSProvider } from '../../contexts/POSContext';
import POSLayout from '../../layouts/POSLayout';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
      style={{ padding: '20px 0' }}
    >
      {value === index && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
}

const POSSettingsContent: React.FC = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  
  // General settings
  const [storeInfo, setStoreInfo] = useState({
    name: 'Fallen Leaf Marina',
    address: '1234 Marina Dr, South Lake Tahoe, CA 96150',
    phone: '(555) 123-4567',
    email: 'info@fallenleafmarina.com',
    website: 'www.fallenleafmarina.com'
  });
  
  // Receipt settings
  const [receiptSettings, setReceiptSettings] = useState({
    printAutomatically: true,
    showLogo: true,
    showItemDetails: true,
    showTaxDetails: true,
    footerMessage: 'Thank you for your business!'
  });
  
  // Tax settings
  const [taxSettings, setTaxSettings] = useState({
    enableTax: false, // Default to disabled until properly configured
    defaultTaxRate: 0, // No default tax rate - must be configured
    enableTaxExemption: true
  });
  
  // Payment settings
  const [paymentSettings, setPaymentSettings] = useState({
    acceptCash: true,
    acceptCreditCards: true,
    acceptChecks: false,
    enableTipping: true,
    defaultTipOptions: [15, 18, 20]
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  const handleStoreInfoChange = (field: string, value: string) => {
    setStoreInfo({
      ...storeInfo,
      [field]: value
    });
  };
  
  const handleReceiptSettingToggle = (field: string) => {
    setReceiptSettings({
      ...receiptSettings,
      [field]: !receiptSettings[field as keyof typeof receiptSettings]
    });
  };
  
  const handleTaxSettingToggle = (field: string) => {
    setTaxSettings({
      ...taxSettings,
      [field]: !taxSettings[field as keyof typeof taxSettings]
    });
  };
  
  const handlePaymentSettingToggle = (field: string) => {
    setPaymentSettings({
      ...paymentSettings,
      [field]: !paymentSettings[field as keyof typeof paymentSettings]
    });
  };
  
  const handleTaxRateChange = (value: string) => {
    setTaxSettings({
      ...taxSettings,
      defaultTaxRate: parseFloat(value) || 0
    });
  };
  
  const handleSaveSettings = () => {
    // Implementation would save settings to backend
    alert('Settings saved successfully!');
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">
          POS Settings
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<SaveIcon />}
          onClick={handleSaveSettings}
        >
          Save Settings
        </Button>
      </Box>
      <Paper sx={{ flexGrow: 1 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="General" icon={<SettingsIcon />} iconPosition="start" />
            <Tab label="Receipt" icon={<ReceiptIcon />} iconPosition="start" />
            <Tab label="Tax" icon={<TaxIcon />} iconPosition="start" />
            <Tab label="Payment" icon={<PaymentIcon />} iconPosition="start" />
            <Tab label="Notifications" icon={<NotificationsIcon />} iconPosition="start" />
          </Tabs>
        </Box>
        
        {/* General Settings */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid xs={12}>
              <Typography variant="h6" gutterBottom>
                Store Information
              </Typography>
            </Grid>
            <Grid xs={12} md={6}>
              <TextField
                fullWidth
                label="Store Name"
                value={storeInfo.name}
                onChange={(e) => handleStoreInfoChange('name', e.target.value)}
              />
            </Grid>
            <Grid xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={storeInfo.phone}
                onChange={(e) => handleStoreInfoChange('phone', e.target.value)}
              />
            </Grid>
            <Grid xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={storeInfo.address}
                onChange={(e) => handleStoreInfoChange('address', e.target.value)}
              />
            </Grid>
            <Grid xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                value={storeInfo.email}
                onChange={(e) => handleStoreInfoChange('email', e.target.value)}
              />
            </Grid>
            <Grid xs={12} md={6}>
              <TextField
                fullWidth
                label="Website"
                value={storeInfo.website}
                onChange={(e) => handleStoreInfoChange('website', e.target.value)}
              />
            </Grid>
          </Grid>
        </TabPanel>
        
        {/* Receipt Settings */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid xs={12}>
              <Typography variant="h6" gutterBottom>
                Receipt Settings
              </Typography>
            </Grid>
            <Grid xs={12}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={receiptSettings.printAutomatically}
                      onChange={() => handleReceiptSettingToggle('printAutomatically')}
                    />
                  }
                  label="Print receipt automatically after sale"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={receiptSettings.showLogo}
                      onChange={() => handleReceiptSettingToggle('showLogo')}
                    />
                  }
                  label="Show store logo on receipt"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={receiptSettings.showItemDetails}
                      onChange={() => handleReceiptSettingToggle('showItemDetails')}
                    />
                  }
                  label="Show detailed item information"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={receiptSettings.showTaxDetails}
                      onChange={() => handleReceiptSettingToggle('showTaxDetails')}
                    />
                  }
                  label="Show tax details"
                />
              </FormGroup>
            </Grid>
            <Grid xs={12}>
              <TextField
                fullWidth
                label="Receipt Footer Message"
                value={receiptSettings.footerMessage}
                onChange={(e) => setReceiptSettings({...receiptSettings, footerMessage: e.target.value})}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </TabPanel>
        
        {/* Tax Settings */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid xs={12}>
              <Typography variant="h6" gutterBottom>
                Tax Settings
              </Typography>
            </Grid>
            <Grid xs={12}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={taxSettings.enableTax}
                      onChange={() => handleTaxSettingToggle('enableTax')}
                    />
                  }
                  label="Enable sales tax"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={taxSettings.enableTaxExemption}
                      onChange={() => handleTaxSettingToggle('enableTaxExemption')}
                    />
                  }
                  label="Allow tax exemption"
                />
              </FormGroup>
            </Grid>
            <Grid xs={12} md={6}>
              <TextField
                fullWidth
                label="Default Tax Rate (%)"
                value={taxSettings.defaultTaxRate}
                onChange={(e) => handleTaxRateChange(e.target.value)}
                type="number"
                InputProps={{
                  inputProps: { min: 0, step: 0.01 }
                }}
                disabled={!taxSettings.enableTax}
              />
            </Grid>
          </Grid>
        </TabPanel>
        
        {/* Payment Settings */}
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            <Grid xs={12}>
              <Typography variant="h6" gutterBottom>
                Payment Methods
              </Typography>
            </Grid>
            <Grid xs={12}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={paymentSettings.acceptCash}
                      onChange={() => handlePaymentSettingToggle('acceptCash')}
                    />
                  }
                  label="Accept Cash"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={paymentSettings.acceptCreditCards}
                      onChange={() => handlePaymentSettingToggle('acceptCreditCards')}
                    />
                  }
                  label="Accept Credit Cards"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={paymentSettings.acceptChecks}
                      onChange={() => handlePaymentSettingToggle('acceptChecks')}
                    />
                  }
                  label="Accept Checks"
                />
              </FormGroup>
            </Grid>
            <Grid xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Tipping Options
              </Typography>
            </Grid>
            <Grid xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={paymentSettings.enableTipping}
                    onChange={() => handlePaymentSettingToggle('enableTipping')}
                  />
                }
                label="Enable tipping"
              />
            </Grid>
          </Grid>
        </TabPanel>
        
        {/* Notification Settings */}
        <TabPanel value={tabValue} index={4}>
          <Typography variant="h6" gutterBottom>
            Notification Settings
          </Typography>
          <Typography variant="body1">
            Configure notification settings for orders, inventory alerts, and more.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This feature is coming soon.
          </Typography>
        </TabPanel>
      </Paper>
    </Box>
  );
};

const POSSettings: React.FC = () => {
  return (
    <POSProvider>
      <POSLayout>
        <POSSettingsContent />
      </POSLayout>
    </POSProvider>
  );
};

export default POSSettings; 